import { useState, useEffect } from 'react';
import { Clock, TrendingUp, Calendar, FileText } from 'lucide-react';
import { secReportsAPI } from '../../lib/api';

interface Report {
  report_id: string;
  filing_type: string;
  fiscal_period: string;
  filing_date: string;
  financial_snapshot?: {
    revenue?: number;
    gross_margin?: number;
    operating_margin?: number;
  };
}

interface CompanyReports {
  company_id: string;
  company_name: string;
  ticker: string;
  reports: Report[];
  total_reports: number;
}

interface HistoricalReportsTimelineProps {
  caseId: string;
  companyId: string;
  companyName: string;
  ticker?: string;
}

export default function HistoricalReportsTimeline({
  caseId,
  companyId,
  companyName,
  ticker,
}: HistoricalReportsTimelineProps) {
  const [reports, setReports] = useState<Report[]>([]);
  const [totalReports, setTotalReports] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchReports = async () => {
      try {
        setLoading(true);
        setError(null);
        const data: CompanyReports = await secReportsAPI.getCompanyReports(caseId, companyId, 8);
        setReports(data.reports || []);
        setTotalReports(data.total_reports || 0);
      } catch (err: any) {
        console.error('Error fetching reports:', err);
        setError(err.response?.data?.detail || 'Failed to load historical reports');
      } finally {
        setLoading(false);
      }
    };

    if (caseId && companyId) {
      fetchReports();
    }
  }, [caseId, companyId]);

  const formatRevenue = (revenue: number | undefined) => {
    if (!revenue) return 'N/A';
    if (revenue >= 1_000_000_000) {
      return `$${(revenue / 1_000_000_000).toFixed(2)}B`;
    } else if (revenue >= 1_000_000) {
      return `$${(revenue / 1_000_000).toFixed(2)}M`;
    } else if (revenue >= 1_000) {
      return `$${(revenue / 1_000).toFixed(2)}K`;
    } else {
      return `$${revenue.toFixed(2)}`;
    }
  };

  const formatPercentage = (value: number | undefined) => {
    if (!value) return 'N/A';
    return `${(value * 100).toFixed(1)}%`;
  };

  const getFilingTypeColor = (filingType: string) => {
    if (filingType.includes('10-K')) return 'bg-blue-100 text-blue-700';
    if (filingType.includes('10-Q')) return 'bg-green-100 text-green-700';
    if (filingType.includes('8-K')) return 'bg-amber-100 text-amber-700';
    return 'bg-neutral-100 text-neutral-700';
  };

  if (loading) {
    return (
      <div className="border border-neutral-200 rounded-lg p-4 bg-white">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-md font-semibold text-neutral-900 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Historical Reports
          </h4>
        </div>
        <div className="text-center py-8 text-neutral-500">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
          Loading historical reports...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="border border-neutral-200 rounded-lg p-4 bg-white">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-md font-semibold text-neutral-900 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Historical Reports
          </h4>
        </div>
        <div className="text-center py-4 text-neutral-500">
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <div className="border border-neutral-200 rounded-lg p-4 bg-white">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-md font-semibold text-neutral-900 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Historical Reports
          </h4>
        </div>
        <div className="text-center py-6 text-neutral-500">
          <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No historical reports found.</p>
          <p className="text-xs mt-1">Reports will appear here after you save company research.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-neutral-200 rounded-lg p-4 bg-white">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-md font-semibold text-neutral-900 flex items-center gap-2">
          <Clock className="w-5 h-5" />
          Historical Reports
        </h4>
        <span className="text-xs text-neutral-500">
          {totalReports} {totalReports === 1 ? 'report' : 'reports'} total
        </span>
      </div>

      <div className="space-y-3">
        {reports.map((report, index) => (
          <div
            key={report.report_id}
            className={`border border-neutral-200 rounded-lg p-3 transition-colors hover:border-primary-300 hover:bg-primary-50 ${
              index === 0 ? 'bg-primary-50 border-primary-200' : 'bg-white'
            }`}
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded text-xs font-medium ${getFilingTypeColor(report.filing_type)}`}>
                  {report.filing_type}
                </span>
                <span className="text-sm font-semibold text-neutral-900">{report.fiscal_period}</span>
                {index === 0 && (
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">Latest</span>
                )}
              </div>
              <div className="flex items-center gap-1 text-xs text-neutral-500">
                <Calendar className="w-3 h-3" />
                {new Date(report.filing_date).toLocaleDateString()}
              </div>
            </div>

            {report.financial_snapshot && (
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="bg-white border border-neutral-200 rounded p-2">
                  <div className="flex items-center gap-1 text-neutral-500 mb-1">
                    <TrendingUp className="w-3 h-3" />
                    <span>Revenue</span>
                  </div>
                  <div className="font-semibold text-neutral-900">
                    {formatRevenue(report.financial_snapshot.revenue)}
                  </div>
                </div>
                <div className="bg-white border border-neutral-200 rounded p-2">
                  <div className="text-neutral-500 mb-1">Gross Margin</div>
                  <div className="font-semibold text-neutral-900">
                    {formatPercentage(report.financial_snapshot.gross_margin)}
                  </div>
                </div>
                <div className="bg-white border border-neutral-200 rounded p-2">
                  <div className="text-neutral-500 mb-1">Op. Margin</div>
                  <div className="font-semibold text-neutral-900">
                    {formatPercentage(report.financial_snapshot.operating_margin)}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {totalReports > reports.length && (
        <div className="mt-3 text-center">
          <p className="text-xs text-neutral-500">
            Showing {reports.length} of {totalReports} reports
          </p>
        </div>
      )}
    </div>
  );
}
